/**
 * Holds the JSON responses the favourites prefetch collects for `/apps/:id` — the snapshot list and
 * the default tab's filter fields — until that view asks for them.
 *
 * Only the prefetch writes here. The view reads a warmed entry when one exists and otherwise
 * requests as it always has, so ordinary navigation gains no caching it did not have before: an app
 * nobody prefetched behaves exactly as it did, and a return visit re-reads rather than replaying.
 *
 * Entries stop being usable `PUBLISHED_APP_PREFETCH_TTL_MS` after the prefetch started them, in
 * flight or not. Bounding the in-flight case matters: without it a prefetch whose request never
 * settles would leave every later view awaiting a promise that resolves for nobody.
 */

import { PUBLISHED_APP_PREFETCH_TTL_MS } from "@/constants";
import type { AppSnapshot } from "@/features/app-studio/api/appSnapshotsApi";
import type { TabFilterField } from "@/features/app-studio/api/appTabFiltersApi";
import { listPublishedTabFilters } from "@/features/published-apps/api/publishedAppFiltersApi";
import { listPublishedAppSnapshots } from "@/features/published-apps/api/publishedAppSnapshotsApi";

type PrefetchedRequest = {
  promise: Promise<unknown>;
  startedAt: number;
};

const prefetchedRequests = new Map<string, PrefetchedRequest>();

function isUsable(entry: PrefetchedRequest): boolean {
  return Date.now() - entry.startedAt < PUBLISHED_APP_PREFETCH_TTL_MS;
}

function prefetch<T>(key: string, load: () => Promise<T>): Promise<T> {
  const existing = prefetchedRequests.get(key);
  if (existing && isUsable(existing)) {
    return existing.promise as Promise<T>;
  }

  const entry: PrefetchedRequest = { promise: Promise.resolve(), startedAt: Date.now() };
  entry.promise = load().catch((error: unknown) => {
    // A failed prefetch must not be handed to the view, which would show an error for a request the
    // viewer never made. Dropping it here sends them down the ordinary request path instead.
    if (prefetchedRequests.get(key) === entry) {
      prefetchedRequests.delete(key);
    }
    throw error;
  });
  prefetchedRequests.set(key, entry);
  // Nothing may await this rejection but the view, and there may be no view: settle it here so a
  // prefetch failure is never an unhandled rejection.
  entry.promise.catch(() => {});
  return entry.promise as Promise<T>;
}

function loadPrefetched<T>(key: string, load: () => Promise<T>): Promise<T> {
  const entry = prefetchedRequests.get(key);
  if (!entry) {
    return load();
  }
  if (!isUsable(entry)) {
    prefetchedRequests.delete(key);
    return load();
  }
  return entry.promise as Promise<T>;
}

function snapshotsKey(appId: number): string {
  return `snapshots:${appId}`;
}

function filterFieldsKey(appId: number, tabId: number | null): string {
  return `filters:${appId}:${tabId ?? "all"}`;
}

/** Warm the app's snapshot list. */
export function prefetchPublishedAppSnapshots(appId: number): Promise<AppSnapshot[]> {
  return prefetch(snapshotsKey(appId), () => listPublishedAppSnapshots(appId));
}

/** Warm one tab's filter fields. `null` is the tab the view opens on. */
export function prefetchPublishedTabFilterFields(
  appId: number,
  tabId: number | null,
): Promise<TabFilterField[]> {
  return prefetch(filterFieldsKey(appId, tabId), () => listPublishedTabFilters(appId, tabId));
}

/** The app's snapshot list, from the prefetch when it warmed one. */
export function loadPublishedAppSnapshots(appId: number): Promise<AppSnapshot[]> {
  return loadPrefetched(snapshotsKey(appId), () => listPublishedAppSnapshots(appId));
}

/** The tab's filter fields with the viewer's effective values, from the prefetch when warmed. */
export function loadPublishedTabFilterFields(
  appId: number,
  tabId: number | null,
): Promise<TabFilterField[]> {
  return loadPrefetched(filterFieldsKey(appId, tabId), () => listPublishedTabFilters(appId, tabId));
}

/**
 * Forget every tab's warmed filter fields for one app. The viewer's saved values are part of what
 * that endpoint returns, so a preference write makes anything warmed beforehand describe the
 * selection they just replaced.
 */
export function invalidatePublishedTabFilterFields(appId: number): void {
  const prefix = `filters:${appId}:`;
  for (const key of [...prefetchedRequests.keys()]) {
    if (key.startsWith(prefix)) {
      prefetchedRequests.delete(key);
    }
  }
}

/** Drop everything warmed. Called when the session ends. */
export function clearPublishedAppViewCache(): void {
  prefetchedRequests.clear();
}
